import { useEffect, useMemo, useState } from "react";
import { HighchartsPanel } from "../charts/HighchartsPanel";
import {
  fetchAdminRegionStats,
  fetchAdminStats,
  type AdminRegionStats,
  type AdminStats,
} from "../api";
import { useAdminLive } from "../hooks/useAdminLive";

function formatCount(n: number) {
  return n.toLocaleString();
}

function RegionSalesGauge({ region }: { region: AdminRegionStats }) {
  const pct = region.sold_pct ?? 0;
  const hasCapacity =
    region.total_pixels != null && region.total_pixels > 0;

  return (
    <div className="admin-region-gauge">
      <div className="admin-region-gauge-head">
        <span className="admin-region-gauge-name">{region.name}</span>
      {region.is_global ? (
        <span className="admin-badge admin-badge-muted">Global</span>
        ) : hasCapacity ? (
          <span className="admin-region-gauge-pct">{pct.toFixed(1)}% sold</span>
        ) : null}
      </div>

      {region.is_global ? (
        <p className="admin-region-gauge-detail">
          {formatCount(region.sold_pixels)} sold outside local regions
        </p>
      ) : hasCapacity ? (
        <>
          <div
            className="admin-region-gauge-track"
            role="meter"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            aria-label={`${region.name}: ${pct.toFixed(1)}% sold`}
          >
            <div
              className="admin-region-gauge-fill"
              style={{ width: `${Math.min(pct, 100)}%` }}
            />
          </div>
          <p className="admin-region-gauge-detail">
            {formatCount(region.sold_pixels)} of{" "}
            {formatCount(region.total_pixels!)}
          </p>
        </>
      ) : (
        <p className="admin-region-gauge-detail">
          Set total pixels in Regions (default 1,000,000)
          {region.sold_pixels > 0
            ? ` · ${formatCount(region.sold_pixels)} sold`
            : ""}
        </p>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <div className="admin-stat-card">
      <div className="admin-stat-label">{label}</div>
      <div className="admin-stat-value">{value}</div>
      {hint ? <div className="admin-stat-hint">{hint}</div> : null}
    </div>
  );
}

export function DashboardPage() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [regionStats, setRegionStats] = useState<AdminRegionStats[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { connectionCount } = useAdminLive();

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const data = await fetchAdminStats();
        if (alive) {
          setStats(data);
          setError(null);
        }
      } catch (err) {
        if (alive) setError((err as Error).message);
      }
    };
    void load();
    const timer = window.setInterval(() => void load(), 60_000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    const loadRegions = async () => {
      try {
        const data = await fetchAdminRegionStats();
        if (alive) setRegionStats(data);
      } catch {
        /* region gauges are non-critical */
      }
    };
    void loadRegions();
    const timer = window.setInterval(() => void loadRegions(), 60_000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, []);

  const soldWeekChart = useMemo(
    () => ({
      chart: { type: "areaspline", backgroundColor: "transparent" },
      title: { text: undefined },
      xAxis: {
        categories: stats?.sold_by_day.map((d) => d.date.slice(5)) ?? [],
      },
      yAxis: { title: { text: "Frames sold" } },
      legend: { enabled: false },
      series: [
        {
          name: "Sold",
          data: stats?.sold_by_day.map((d) => d.count) ?? [],
          color: "#3b6e81",
        },
      ],
      plotOptions: {
        areaspline: { fillOpacity: 0.2, marker: { enabled: false } },
      },
    }),
    [stats?.sold_by_day],
  );

  const soldMonthChart = useMemo(
    () => ({
      chart: { type: "column", backgroundColor: "transparent" },
      title: { text: undefined },
      xAxis: { categories: stats?.sold_by_month.map((d) => d.month) ?? [] },
      yAxis: { title: { text: "Frames sold" } },
      legend: { enabled: false },
      series: [
        {
          name: "Sold",
          data: stats?.sold_by_month.map((d) => d.count) ?? [],
          color: "#6eb5d0",
        },
      ],
    }),
    [stats?.sold_by_month],
  );

  const statusChart = useMemo(() => {
    if (!stats) return null;
    const labels: Record<string, string> = {
      "0": "Available",
      "1": "Reserved",
      "2": "Claimed",
      "3": "Sold",
    };
    const data = Object.entries(stats.pixels_by_status).map(([k, v]) => ({
      name: labels[k] ?? `Status ${k}`,
      y: v,
    }));
    return {
      chart: { type: "pie", backgroundColor: "transparent" },
      title: { text: undefined },
      series: [{ name: "Pixels", data, colorByPoint: true }],
      plotOptions: {
        pie: {
          innerSize: "55%",
          dataLabels: { enabled: true, style: { color: "#dce8f2" } },
        },
      },
    };
  }, [stats]);

  const revenueText =
    stats?.revenue
      .map((r) => `${r.currency} ${r.amount.toFixed(2)} (${r.orders})`)
      .join(" · ") || "—";

  const sortedRegionStats = useMemo(() => {
    const local = regionStats.filter((r) => !r.is_global);
    const global = regionStats.filter((r) => r.is_global);
    return [...local, ...global];
  }, [regionStats]);

  return (
    <div>
      <h1 className="admin-page-title">Dashboard</h1>
      <p className="admin-page-sub">
        Live overview of sales, inventory, and connected users.
      </p>

      {error ? <p className="admin-error">{error}</p> : null}

      <div className="admin-stats-grid">
        <StatCard label="Sold frames" value={stats?.sold_total ?? "—"} />
        <StatCard label="Reserved" value={stats?.reserved_total ?? "—"} />
        <StatCard
          label="Live connections"
          value={connectionCount}
          hint="WebSocket clients now"
        />
        <StatCard
          label="Active users"
          value={stats?.active_users ?? "—"}
          hint="Signed-in sessions"
        />
        <StatCard label="Registered users" value={stats?.users_total ?? "—"} />
        <StatCard label="Buyers" value={stats?.buyers_total ?? "—"} />
        <StatCard label="Paid orders" value={stats?.orders_paid ?? "—"} />
        <StatCard label="Revenue" value={revenueText} />
      </div>

      <div className="admin-charts-grid">
        <div className="admin-panel">
          <h3>Sold — last 30 days</h3>
          <HighchartsPanel options={soldWeekChart} />
        </div>
        <div className="admin-panel">
          <h3>Sold — last 12 months</h3>
          <HighchartsPanel options={soldMonthChart} />
        </div>
        {statusChart ? (
          <div className="admin-panel">
            <h3>Pixel inventory</h3>
            <HighchartsPanel options={statusChart} height={300} />
          </div>
        ) : null}
      </div>

      {sortedRegionStats.length > 0 ? (
        <div className="admin-panel" style={{ marginBottom: "1.5rem" }}>
          <h3>Region sales</h3>
          <p
            style={{
              color: "#8ba3b8",
              fontSize: "0.85rem",
              margin: "0 0 1rem",
            }}
          >
            Sold share within each region polygon. Set total pixel capacity in
            Regions. Overlapping regions may double-count the same pixel.
          </p>
          <div className="admin-region-gauge-grid">
            {sortedRegionStats.map((region) => (
              <RegionSalesGauge key={region.id} region={region} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
